import { isObject, mapValues } from 'lodash';
import { countOccurrences } from './string.helpers';
import { includesAll } from './array.helpers';
import {
  filterPropertiesByType,
  getKeys,
  isNonArrayObject,
  pick,
} from './object.helpers';
import {
  ArrayOrRecord,
  Merge,
  NotArray,
  TypeFilter,
} from '../models/type-helpers.model';
import {
  isValidNumber,
  validateFiniteNumber,
} from '../validation/number.validation';

/**
 * Round a number to a given number of decimals.
 * @param value Number to round
 * @param decimals Number of decimals to round to, defaults to 2
 * @returns Rounded number
 */
export const roundToDecimals = (value: number, decimals = 2): number =>
  Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);

/**
 * Test if a string can be converted into a number by using +str
 * @param str
 * @returns
 */
export const isNumeric = (str: unknown): boolean =>
  typeof str === 'string' || typeof str === 'number' ? isFinite(+str) : false;

/**
 * Limit a value between a min and max.
 * If min or max is not provided that limit is ignored.
 * @param num
 * @param min
 * @param max
 * @returns
 */
export const clamp = (
  num: number,
  min: number | undefined,
  max: number | undefined,
): number => {
  if (min !== undefined && max !== undefined && min > max) {
    throw new Error('Min cannot be greater than max');
  }
  if (min !== undefined) {
    num = Math.max(num, min);
  }
  if (max !== undefined) {
    num = Math.min(num, max);
  }

  return num;
};

/**
 * Get highest value of  an array of objects containing a number property.
 * Note that is will only check numbers and ignore other properties
 */
export function highest(values: number[]): number;
export function highest<T extends object, K extends keyof T>(
  values: T[],
  key: K,
): number;
export function highest<T extends object | number, K extends keyof T>(
  values: T[],
  key?: K,
): number {
  return values.reduce((acc, curr) => {
    const value = typeof curr === 'number' ? curr : key && curr[key];
    return typeof value === 'number' ? Math.max(acc, value) : acc;
  }, 0);
}

/**
 * Sum an array of objects containing a number property.
 * Note that is will only sum numbers and ignore other properties
 */
export function sum(values: (number | undefined)[]): number;
export function sum(object: object): number;
export function sum<T extends object>(
  values: T[],
  keyOrMapFn?: (value: T) => number,
): number;
export function sum<T extends object, K extends keyof Merge<T>>(
  values: T[],
  key: K,
): number;
export function sum<T extends object | number, K extends keyof T>(
  values: T[] | object,
  keyOrMapFn?: K | ((value: T) => number),
): number {
  // If key is a function then we are mapping the values
  if (typeof keyOrMapFn === 'function') {
    return sum(Object.values(values).map(keyOrMapFn));
  }
  if (!Array.isArray(values) && isObject(values)) {
    return sum(Object.values(values).filter(Number.isFinite)); // Filter out non-finite values like strings, NaN, etc
  }
  return (values as T[]).reduce((acc, curr) => {
    const value =
      typeof curr === 'number' ? curr : keyOrMapFn && curr[keyOrMapFn];
    return acc + (typeof value === 'number' && isFinite(value) ? value : 0);
  }, 0);
}

/**
 * Sum all properties in an array/record of objects.
 * @param objects Array of objects to sum.
 * @returns An object with each property summed.
 */
export const sumObjects = <T extends object, R extends TypeFilter<T, 'number'>>(
  objects: ArrayOrRecord<T>,
): R => {
  return Object.values(objects).reduce((acc, obj) => {
    const filtered = filterPropertiesByType(obj, 'number');
    // Numeric view of acc: TS 7 types indexed writes with a union key as an
    // intersection, which rejects the per-key cast used before
    const sums = acc as Record<keyof typeof filtered, number>;
    getKeys(filtered).forEach((key) => {
      const value = filtered[key];
      if (isValidNumber(value)) {
        sums[key] = (sums[key] ?? 0) + value;
      }
    });
    return acc;
  }, {} as R);
};

/**
 * Sum properties in an object. If no keys are provided, all properties are summed.
 * @param object
 * @param keys properties to sum, defaults to all properties
 * @returns
 */
export const sumProperties = <T extends object>(
  object: T,
  ...keys: (keyof T)[]
): number => {
  return sum(Object.values(keys.length ? pick(object, ...keys) : object));
};

/**
 * Multiply all properties with the same keys in an array of objects.
 * @param objects Array of objects to multiply.
 * @returns An object with each property multiplied.
 * @example
 * multiplyObjects({ a: 1, b: 2 }, { a: 3, b: 4 }) // { a: 3, b: 8 }
 */
export const multiplyObjects = <
  T extends object,
  R extends TypeFilter<T, 'number'>,
>(
  ...objects: T[]
): R => {
  return objects.reduce((acc, obj) => {
    const filtered = filterPropertiesByType(obj, 'number');
    // Numeric view of acc: TS 7 types indexed writes with a union key as an
    // intersection, which rejects the per-key cast used before
    const products = acc as Record<keyof typeof filtered, number>;
    getKeys(filtered).forEach((key) => {
      const rawValue = filtered[key];
      const value = isValidNumber(rawValue) ? rawValue : 0;
      products[key] = (products[key] ?? 1) * value;
    });
    return acc;
  }, {} as R);
};

/**
 * Multiply an array of numbers or objects containing a number property.
 * Note that is will only multiply numbers and ignore other properties
 */
export function multiply(values: number[]): number;
export function multiply<T extends object, K extends keyof T>(
  values: T[],
  key: K,
): number;
export function multiply<T extends object | number, K extends keyof T>(
  values: T[],
  key?: K,
): number {
  return values.reduce((acc, curr) => {
    const value = typeof curr === 'number' ? curr : key && curr[key];
    return acc * (typeof value === 'number' ? value : 1);
  }, 1);
}

/**
 * Make sure the type is used as number.
 * @param value The value to convert to number
 * @param defaultValue
 * @returns
 */
export const getValueAsNumber = (
  value: boolean | string | number | undefined,
  defaultValue = 0,
): number => {
  return value !== undefined && value !== null && isFinite(+value)
    ? +value
    : defaultValue;
};

/**
 * Multiply any number properties in an object by a factor.
 * If a property is not a number, it is kept unmodified.
 */
export function multiplyProperties<T extends object, K extends keyof T>(
  obj: T,
  factor: number,
  ...exclude: K[]
): T {
  if (!isNonArrayObject(obj) || factor === 1) {
    return obj;
  }

  const result = mapValues(obj, (value, key) => {
    return typeof value === 'number' && !exclude.includes(key as K)
      ? value * factor
      : value;
  });

  return result as T;
}

/**
 * Get all properties in an object as numbers
 * @param object
 * @returns
 */
export const toNumberObject = <T extends object, K extends keyof T>(
  object: T,
  defaultValue?: number,
): Record<K, number> => {
  return mapValues(object, (value) => toNumber(value, defaultValue));
};

/**
 * Convert value to number or return default value if it can't be done.
 * Can handle strings like '1 000' and '1,000.00' and '1,000,000.12' and '1px' and '1.2px'
 * Will also make sure a number value can't be NaN, Infinity or -Infinity.
 * @param value
 * @param defaultValue
 * @returns
 */
export const toNumber = (value: unknown, defaultValue = 0): number => {
  // Remove formatting to support strings like '1 000'
  if (typeof value === 'string') {
    value = value.trim().replace(/[^\d]+$/g, '');
    value = removeNumberFormatting(value);

    // Empty should be converted to default
    if (!value) {
      return toNumber(defaultValue);
    }
  }

  switch (typeof value) {
    case 'string':
    case 'boolean':
    case 'number': {
      const converted = +value;
      return isFinite(converted) ? converted : toNumber(defaultValue);
    }
  }
  return toNumber(defaultValue);
};

/**
 * Validate a string that looks and can be parsed as a plain number
 * @param str
 * @returns
 */
export const validatePlainNumberString = (str: string): boolean => {
  return /^[+-]?(\d*\.)?\d+$/.test(str) && Number.isFinite(parseFloat(str));
};
/**
 * Validate a string that looks and can be parsed as an exponential number
 * @param str
 * @returns
 */
export const validateExponentNumberString = (str: string): boolean => {
  return (
    /^[+-]?(\d*\.)?\d+[eE][+-]?\d+$/.test(str) &&
    Number.isFinite(parseFloat(str))
  );
};

/**
 * Make number strings parsable as Number
 * Will return original string if it cannot be handled.
 * Will turn 1 000 into 1000, 1,000.00 into 1000.00
 * and 1,000,000.12 into 1000000.12
 * @param str
 * @returns
 */
export const removeNumberFormatting = <T = string>(input: T): T => {
  if (input === '') return input; // noop

  // Cases we shouldn't try to fix (can only remove formatting from strings looking like valid numbers)
  if (
    typeof input !== 'string' ||
    !/^-?[\d.,\s\u00A0\u2002\u2003\u2009\u200A]+$/g.test(input)
  ) {
    return input;
  }

  let str: string = input;

  // Remove all commas if there is a dot or there are more than one comma
  if (
    (str.includes('.') && str.lastIndexOf(',') < str.lastIndexOf('.')) ||
    countOccurrences(str, ',') > 1
  ) {
    str = str.replaceAll(',', '');
  }
  str = str
    .replace(/\s/g, '') // Remove spaces
    .replace(/(?<=\d),(?=\d)/g, '.'); // Replace comma between numbers with dot

  // Remove all dots except the first one
  while (countOccurrences(str, '.') > 1) {
    str = str.replace('.', '');
  }
  return str as T;
};

export const getMedianValue = (array: number[], position = 0.5): number => {
  const sorted = [...array].sort((a, b) => b - a).reverse();
  const middleIndex = (sorted.length - 1) * position;
  const rounded = sorted[Math.round(middleIndex)];
  const floored = sorted[Math.floor(middleIndex)];
  const ceiled = sorted[Math.ceil(middleIndex)];

  // Exactly on a number
  if (middleIndex % 1 === 0 && rounded !== undefined) {
    return rounded;
  } else if (floored !== undefined && ceiled !== undefined) {
    return (floored + ceiled) / 2;
  }
  return NaN;
};

/**
 * Get the mean value of an array of numbers or objects containing a number property.
 * @param array
 * @returns
 */
export const getMean = <
  T extends number | NotArray<object>,
  O = TypeFilter<Exclude<T, number>, 'number'>,
  R = T extends number ? number : O,
>(
  ...array: T[]
): R => {
  if (array.length === 0) {
    return 0 as R;
  }
  const numbers: number[] = array.filter((item) => typeof item === 'number');

  if (numbers.length > 0) {
    if (numbers.length !== array.length) {
      throw new Error('Array contains both numbers and non-numbers');
    }
    return validateFiniteNumber(sum(numbers) / numbers.length) as R;
  }

  const sumObj = sumObjects(array as Exclude<T, number>[]);
  return multiplyProperties(sumObj, 1 / array.length) as R;
};

export const factorToPercentage = (factor: number): number => factor * 100;

export const percentageToFactor = (percentage: number): number =>
  percentage / 100;

/**
 * Check if two numbers are close to each other.
 * @param a
 * @param b
 * @param precision How large the difference can be
 * @returns
 */
export const isCloseTo = (
  a: number | undefined,
  b: number | undefined,
  precision = 0.0001,
): boolean => {
  if (a === undefined || b === undefined) {
    return false;
  }
  return Math.abs(a - b) <= precision;
};

/**
 * Returns true if two numbers are within the given percentage of each other.
 * Uses the smaller value as base: min * (1 + percentage) >= max.
 * When percentage is omitted, uses 1% tolerance (0.01).
 *
 * @param a First number
 * @param b Second number
 * @param percentage Tolerance as a fraction (e.g. 0.05 for 5%). Default 0.01.
 * @returns true if a and b are considered close within the tolerance
 */
export const isCloseToByPercent = (
  a: number,
  b: number,
  percentage: number = 0.01,
): boolean => {
  if (a === b) {
    return true;
  }
  const min = Math.min(a, b);
  const max = Math.max(a, b);
  return min * (1 + percentage) >= max;
};

/**
 * Compare two objects by their numeric properties.
 * Returns true if both records have the same keys and each numeric value pair is within the given percentage.
 * Non-numeric values are compared by type equality (both must be number or both non-number).
 * When percentage is omitted, uses 1% tolerance (0.01).
 *
 * @param recordA First object to compare
 * @param recordB Second object to compare
 * @param factor Tolerance as a factor (e.g. 0.05 for 5%). Default 0.01.
 * @returns true if records are considered equal within tolerance
 */
export const isNumberObjectsCloseTo = (
  recordA: object | undefined,
  recordB: object | undefined,
  factor: number = 0.01,
): boolean => {
  if (!isNonArrayObject(recordA) || !isNonArrayObject(recordB)) {
    return recordA === recordB;
  }
  const keysA = getKeys(recordA);
  const keysB = getKeys(recordB);

  if (!includesAll(keysA, ...keysB)) {
    return false;
  }
  return keysA.every((key) => {
    const valueA = recordA[key];
    const valueB = recordB[key];
    const isNumberA = typeof valueA === 'number';
    const isNumberB = typeof valueB === 'number';

    // At least one of them is not a number
    if (!isNumberA || !isNumberB) {
      // If both are not numbers it's still considered equal
      return isNumberA === isNumberB;
    }
    return isCloseToByPercent(valueA, valueB, factor);
  });
};

export const hasDecimals = (value: number): boolean => getDecimals(value) !== 0;

/**
 * Get all decimals of a number.
 * 0.123 -> 123
 * @param value
 * @returns
 */
export const getDecimals = (value: number | string): number => {
  const string = value.toString();

  if (isNumeric(value) && string.includes('.')) {
    return Number.parseFloat(string.split('.')[1] ?? '0') || 0;
  }

  return 0;
};

export const getMaxValuesInArray = <
  T extends object,
  R extends TypeFilter<T, 'number'>,
>(
  array: (T | undefined)[],
): R =>
  array.reduce((acc, cur): R => {
    if (!isObject(cur)) {
      return acc;
    }
    getKeys(cur).forEach((key) => {
      const value = cur[key];
      if (typeof value === 'number' && isFinite(value)) {
        const accValue = acc[key as any as keyof R];
        const numberValue =
          typeof accValue === 'number' && isFinite(accValue) ? accValue : 0;

        acc[key as any as keyof R] = Math.max(numberValue, value) as R[keyof R];
      }
    });

    return acc;
  }, {} as R);

export const toFixedLengthInteger = (
  value: number | string,
  length: number,
): string => {
  const integer = clamp(
    parseInt(value.toString()),
    0,
    Math.pow(10, length) - 1,
  );
  return ('0000000000000' + integer).slice(-length);
};

/**
 * Invert a number, IE. 2 => 1 / 2, 1/2 => 2. If the number is 0, it will return 0.
 * @param number
 * @returns
 */
export const invertNumber = (number: number): number =>
  !number ? 0 : 1 / number;
